import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from "@angular/common/http";
import {
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
} from "@angular/core";
import {DecimalPipe} from "@angular/common";
import {
  provideRouter,
  TitleStrategy,
  withInMemoryScrolling,
  withViewTransitions,
} from "@angular/router";
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import {TranslateHttpLoader} from "@ngx-translate/http-loader";
import {MainConfigurationService} from "./shared/services/main-configuration.service";
import {UserService} from "./shared/services/user.service";
import {PendingChangesGuard} from "./core/guards/pending-changes.guard";
import {AppToastService} from "./shared/services/app-toast.service";
import {FirebaseService} from "./+messaging/shared/services/firebase.service";
import {RouteService} from "./shared/services/route.service";
import {BranchService} from "./shared/services/branch.service";
import {ErrorHandlerService} from "./shared/services/error-handler.service";
import {ConfirmDialogService} from "./core/components/dialog/confirm-dialog/services/confirm-dialog.service";
import {CategoriesAndProductsTreeService} from "./shared/services/categories-and-products-tree.service";
import {PushNotificationsService} from "./shared/services/push-notifications.service";
import {EmployeeService} from "./shared/services/employee.service";
import {OrderService} from "./+order/shared/services/order.service";
import {SaleLineManagementService} from "./+capillary/shared/services/sale-line-management.service";
import {SaleLineFiltersService} from "./+capillary/shared/services/sale-line-filters.service";
import {ReminderService} from "./+reminder/shared/services/reminder.service";
import {CustomerService} from "./+customer/shared/services/customer.service";
import {MAT_DIALOG_DEFAULT_OPTIONS} from "@angular/material/dialog";
import {provideToastr} from "ngx-toastr";
import {
  BrowserAnimationsModule,
  provideAnimations,
} from "@angular/platform-browser/animations";
import {NgxSpinnerModule} from "ngx-spinner";
import {NgxSkeletonLoaderModule} from "ngx-skeleton-loader";
import {ServiceWorkerModule} from "@angular/service-worker";
import {SharedModule} from "./shared/shared.module";
import {BrowserModule} from "@angular/platform-browser";
import {routes} from "./app.routes";
import {BaseInterceptor} from "./core/interceptors/base.interceptor";
import {AuthorizationInterceptor} from "./core/interceptors/authorization.interceptor";
import {StorageService} from "./shared/services/storage.service";
import {ScreenSizeService} from "./shared/services/screen-size.service";
import {LayoutService} from "./layout/shared/services/layout.service";
import {provideCharts, withDefaultRegisterables} from "ng2-charts";
import {SocketFactory} from "./shared/services/socket-factory.service";
import {WalletService} from "./+customer/shared/services/wallet.service";
import {ProductKardexService} from "./+warehouse/shared/services/product-kardex.service";
import {RequestService} from "./+requests/shared/services/request.service";
import {EmergencyService} from "./shared/services/emergency.service";
import {ReturnRequestService} from "./+return-requests/shared/services/return-request.service";
import {TicketManagementService} from "./+ticket-management/shared/services/ticket-management.service";
import {CustomTitleStrategy} from "./layout/shared/overrides/custom-title-strategy.class";

export function HttpLoaderFactory(http: HttpClient) {
  const time = new Date().getTime();
  return new TranslateHttpLoader(http, "./assets/i18n/", `.json?v=${time}`);
}

export const provideTranslation = () => ({
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpClient],
  },
});

const inMemoryScrollingFeature = withInMemoryScrolling({
  scrollPositionRestoration: "top",
  anchorScrolling: "enabled",
});

export const appConfig: ApplicationConfig = {
  providers: [
    // providers
    provideZoneChangeDetection({eventCoalescing: true}),
    provideRouter(routes, inMemoryScrollingFeature, withViewTransitions()),
    provideToastr(),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([BaseInterceptor, AuthorizationInterceptor])
    ),
    provideCharts(withDefaultRegisterables()),
    importProvidersFrom([
      TranslateModule.forRoot(provideTranslation()),
      NgxSpinnerModule.forRoot(),
      NgxSkeletonLoaderModule.forRoot({
        animation: "pulse",
        loadingText: "Loading...",
      }),
      ServiceWorkerModule.register("ngsw-worker.js", {
        enabled: !isDevMode(),
        registrationStrategy: "registerWhenStable:30000",
      }),
    ]),
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {hasBackdrop: true, direction: "rtl"},
    },
    {provide: TitleStrategy, useClass: CustomTitleStrategy},
    // services
    SharedModule,
    BrowserModule,
    BrowserAnimationsModule,
    ScreenSizeService,
    LayoutService,
    TranslateService,
    MainConfigurationService,
    UserService,
    WalletService,
    PendingChangesGuard,
    AppToastService,
    FirebaseService,
    RouteService,
    BranchService,
    ErrorHandlerService,
    ConfirmDialogService,
    CategoriesAndProductsTreeService,
    PushNotificationsService,
    EmployeeService,
    OrderService,
    SaleLineManagementService,
    SaleLineFiltersService,
    ReminderService,
    CustomerService,
    StorageService,
    DecimalPipe,
    SocketFactory,
    ProductKardexService,
    RequestService,
    ReturnRequestService,
    EmergencyService,
    TicketManagementService,
  ],
};
